/**
 * SWIPA signed-wallet API (Phase 2 §5). Подписывает операции клиентским
 * ed25519-ключом (см. lib/wallet/walletKey.ts) и шлёт на /api/wallet/*.
 */
import { apiClient } from './client';
import {
  getOrCreateWalletKey, signMessage, exportPrivPkcs8Hex,
  importAndStoreKey, hexToBuf, hasWalletKey,
} from '@/lib/wallet/walletKey';
import { resolveSubunits } from '@/lib/swipaAmount';

// canonical — БАЙТ-В-БАЙТ с backend/src/services/wallet.service.ts canonical()
// (фиксированный порядок ключей). Любое расхождение → подпись не сойдётся.
function canonical(m: {
  type: string; fromUserId: string; amountSub: string;
  purpose: string; ref: string; nonce: string; ts: number;
}): string {
  return JSON.stringify({
    amountSub: m.amountSub,
    fromUserId: m.fromUserId,
    nonce: m.nonce,
    purpose: m.purpose,
    ref: m.ref,
    ts: m.ts,
    type: m.type,
  });
}

function randHex(n: number): string {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return Array.from(a).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export const walletApi = {
  /** Enroll клиентского pubkey на сервер. Идемпотентно (перезаписывает node). */
  async enroll(userId: string): Promise<void> {
    const key = await getOrCreateWalletKey(userId);
    await apiClient.post('/api/wallet/key', { publicKey: key.publicKeyHex });
  },

  /** Recovery-бэкап (D1): шифруем privkey ключом из пароля и кладём на сервер. */
  async backup(userId: string, password: string): Promise<void> {
    const key = await getOrCreateWalletKey(userId);
    const pkcs8Hex = await exportPrivPkcs8Hex(key.privateKey);
    const { ciphertextHex, ivHex } = await encryptWithPassword(pkcs8Hex, password);
    await apiClient.post('/api/wallet/key', {
      publicKey: key.publicKeyHex, keyBackup: ciphertextHex, keyBackupIv: ivHex,
    });
  },

  /** Восстановление ключа из серверного бэкапа (новое устройство, по паролю). */
  async restore(userId: string, password: string): Promise<boolean> {
    try {
      const { data } = await apiClient.get('/api/wallet/key/backup');
      if (!data?.keyBackup) return false;
      const pkcs8Hex = await decryptWithPassword(data.keyBackup, data.keyBackupIv, password);
      await importAndStoreKey(userId, pkcs8Hex, data.publicKey);
      return true;
    } catch {
      return false;
    }
  },

  /** Подписанное списание (§5.1). userId — из authStore (передаём, чтобы не плодить import cycle). */
  async debitSigned(userId: string, input: { amountSub: string; purpose: string; ref: string; memo?: string }) {
    const key = await getOrCreateWalletKey(userId);
    const msg = {
      type: 'debit',
      fromUserId: userId,
      amountSub: input.amountSub,
      purpose: input.purpose,
      ref: input.ref,
      nonce: randHex(16),
      ts: Math.floor(Date.now() / 1000),
    };
    const signature = await signMessage(key.privateKey, canonical(msg));
    const { data } = await apiClient.post('/api/wallet/debit-signed', { ...msg, memo: input.memo, signature });
    const raw = data as {
      ok: boolean;
      txHash: string;
      status: string;
      balanceSub?: number;
      balanceSubExact?: string;
      idempotent?: boolean;
    };
    return { ...raw, balanceSubExact: resolveSubunits(raw.balanceSubExact, raw.balanceSub) };
  },

  /**
   * Гарантировать, что у этой сессии есть рабочий wallet-ключ + сервер знает pubkey.
   * Модель: ОДИН ключ на юзера, общий между устройствами через зашифр. бэкап.
   *  - есть пароль (login) + нет ключа в IDB → restore из бэкапа (новое устройство),
   *    иначе generate+enroll+backup (первое устройство).
   *  - есть ключ в IDB → enroll (идемпотентно) + (если пароль) обновить бэкап.
   *  - нет пароля (init/refresh) и нет ключа → no-op (дождёмся следующего login).
   * Fire-and-forget — не блокирует auth.
   */
  async ensureWallet(userId: string, password?: string): Promise<void> {
    try {
      if (await hasWalletKey(userId)) {
        await walletApi.enroll(userId);
        if (password) await walletApi.backup(userId, password);
      } else if (password) {
        const restored = await walletApi.restore(userId, password);
        if (!restored) {
          await walletApi.enroll(userId);
          await walletApi.backup(userId, password);
        }
      }
    } catch {
      /* noop — кошелёк best-effort, не ломает вход */
    }
  },

  async balance() {
    const { data } = await apiClient.get('/api/wallet/balance');
    const raw = data as {
      confirmed?: number;
      pending?: number;
      available?: number;
      confirmedSub?: string;
      pendingSub?: string;
      availableSub?: string;
    };
    return {
      ...raw,
      confirmedSub: resolveSubunits(raw.confirmedSub, raw.confirmed),
      pendingSub: resolveSubunits(raw.pendingSub, raw.pending),
      availableSub: resolveSubunits(raw.availableSub, raw.available),
    };
  },
  async history(limit = 30) {
    const { data } = await apiClient.get(`/api/wallet/history?limit=${limit}`);
    return data.history;
  },
  async cancelUnclaimed(ref: string) {
    const { data } = await apiClient.post('/api/wallet/payments/cancel', { ref });
    return data as { ok: boolean; status: string; idempotent?: boolean };
  },
};

// ── password-derived AES-GCM (recovery backup, D1) ────────────────────────────
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: 200_000, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}
function toHex(a: Uint8Array): string {
  return Array.from(a).map((b) => b.toString(16).padStart(2, '0')).join('');
}
// ivHex = salt(16B=32hex) ‖ iv(12B=24hex). Сервер хранит как непрозрачную строку.
async function encryptWithPassword(plaintextHex: string, password: string): Promise<{ ciphertextHex: string; ivHex: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, new TextEncoder().encode(plaintextHex));
  return { ciphertextHex: toHex(new Uint8Array(ct)), ivHex: toHex(salt) + toHex(iv) };
}
async function decryptWithPassword(ciphertextHex: string, ivHex: string, password: string): Promise<string> {
  const salt = new Uint8Array(hexToBuf(ivHex.slice(0, 32)));
  const iv = new Uint8Array(hexToBuf(ivHex.slice(32, 56)));
  const key = await deriveKey(password, salt);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, hexToBuf(ciphertextHex));
  return new TextDecoder().decode(pt);
}
